import { Box, Container, Flex, HStack, Image, Link, Text, VStack, useTheme } from '@chakra-ui/react'
import { useIsApplyDialog } from '@core/utils/contexts/ApplyDialogContext/ApplDialogContext'
import { Localization } from '@core/app/types/globalApiType'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

export const Footer = (): JSX.Element => {
    const { t } = useTranslation()
    const { appTheme } = useAppTheme()
    const theme = useTheme()

    const logoPrefix = appTheme ? appTheme.logoPrefix : undefined
    const corporation = appTheme ? appTheme.corporation : undefined
    const mainThemeBgColor =
        appTheme && appTheme?.newSettings?.general?.FooterBackground
            ? `#${appTheme?.newSettings?.general?.FooterBackground}`
            : theme.colors.bg[4]
    const mainThemeHeadingColor =
        appTheme && appTheme?.newSettings?.general?.FooterHeadingText
            ? `#${appTheme?.newSettings?.general?.FooterHeadingText}`
            : theme.colors.text.white
    const mainThemeTextColor =
        appTheme && appTheme?.newSettings?.general?.FooterText
            ? `#${appTheme?.newSettings?.general?.FooterText}`
            : theme.colors.text.white
    const { isApplyDialog } = useIsApplyDialog()
    const [localization, setLocalization] = useState<Localization>({})

    useEffect(() => {
        if (appTheme && appTheme?.localization) {
            setLocalization(appTheme?.localization)
        }
    }, [appTheme])

    return (
        <Box
            as={'footer'}
            pt={{ base: 4, xl: 12 }}
            bg={mainThemeBgColor ? mainThemeBgColor : 'bg.1'}
            pb={{ base: isApplyDialog ? '166px' : '83px', xl: 0 }}
            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
        >
            <Container maxW={{ base: 'container.md', xl: 'container.xl' }}>
                <Flex
                    flexDirection={{ base: 'column', xl: 'row' }}
                    alignItems={'start'}
                    justifyContent={'space-between'}
                    pb={10}
                    borderBottom={'1px solid'}
                    borderColor={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                >
                    <Flex
                        flexDirection={{ base: 'column', xl: 'row' }}
                        alignItems={'start'}
                        gap={{ base: 6, xl: 0 }}
                        mb={{ base: '32px', xl: 0 }}
                    >
                        {logoPrefix && corporation && corporation.FooterLogo && (
                            <Link href={'/'} aria-label={`${t('141')}`}>
                                <Image
                                    src={`${logoPrefix}${corporation.FooterLogo}`}
                                    maxW={{ base: '120px', xl: '200px' }}
                                    width={{ base: 120, xl: 200 }}
                                    mr={{ base: 0, xl: '110px' }}
                                    alt={`${t('1464')}`}
                                />
                            </Link>
                        )}

                        <VStack alignItems={'start'} maxW={{ base: 'auto', xl: '560px' }}>
                            <Text
                                fontSize={'sm'}
                                fontWeight={'semibold'}
                                color={mainThemeHeadingColor ? mainThemeHeadingColor : 'text.main'}
                            >
                                {corporation && corporation.Name ? corporation.Name : ''}
                            </Text>
                            <Text
                                fontSize={'xs'}
                                whiteSpace={'pre-line'}
                                wordBreak={'break-word'}
                                color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                            >
                                {localization[25]
                                    ? localization[25]
                                    : corporation && corporation.About
                                      ? corporation.About
                                      : ''}
                            </Text>
                            {/* <Box
                                as={'a'}
                                color={'text.link'}
                                cursor={'pointer'}
                                display={'flex'}
                                mt={3}
                            >
                                <Text
                                    fontSize={'sm'}
                                    fontWeight={'semibold'}
                                    mr={'1.5'}
                                >
                                    RecMan {t('215')}
                                </Text>
                                <LinkArrowIcon
                                    width={3}
                                    height={3}
                                    color={'text.link'}
                                />
                            </Box> */}
                        </VStack>
                    </Flex>
                    <VStack alignItems={'start'} gap={8} maxW={{ base: '100%', xl: '200px' }}>
                        <VStack alignItems={'start'}>
                            {(corporation && corporation.OfficePhone) || (corporation && corporation.Email) ? (
                                <Text
                                    fontSize={'sm'}
                                    color={mainThemeHeadingColor ? mainThemeHeadingColor : 'text.main'}
                                    fontWeight={'medium'}
                                >
                                    {t('2762')}
                                </Text>
                            ) : null}

                            {corporation && corporation.OfficePhone && (
                                <Text fontSize={'xs'} color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}>
                                    {t('2')}: {corporation.OfficePhone}
                                </Text>
                            )}
                            {corporation && corporation.Email && (
                                <Text fontSize={'xs'} color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}>
                                    {t('92')}: {corporation.Email}
                                </Text>
                            )}
                        </VStack>
                        <VStack alignItems={'start'}>
                            {corporation &&
                            (corporation.Facebook ||
                                corporation.Twitter ||
                                corporation.Linkedin ||
                                corporation.Instagram) ? (
                                <Text
                                    fontSize={'sm'}
                                    color={mainThemeHeadingColor ? mainThemeHeadingColor : 'text.main'}
                                    fontWeight={'medium'}
                                >
                                    {t('2494')}
                                </Text>
                            ) : null}

                            <Flex gap={4} alignItems={'center'}>
                                {corporation && corporation.Facebook && (
                                    <Link href={corporation.Facebook} maxW={6} aria-label="Facebook">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.6607 12.7894V21H13.8427V12.8H16.4949L16.8903 9.59308H13.8427V7.55146C13.8427 6.62385 14.091 5.9869 15.3665 5.9869H17V3.12456C16.7177 3.08569 15.7491 3 14.6224 3C12.2713 3 10.6607 4.48947 10.6607 7.22724V9.59043H8V12.7894H10.6607Z"
                                                fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            />
                                        </svg>
                                    </Link>
                                )}
                                {corporation && corporation.Linkedin && (
                                    <Link href={corporation.Linkedin} maxW={6} aria-label="LinkedIn">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M20.4469 20.4519H16.8929V14.8829C16.8929 13.5549 16.8659 11.8459 15.0409 11.8459C13.1879 11.8459 12.9049 13.2909 12.9049 14.7849V20.4519H9.35093V8.99993H12.7649V10.5609H12.8109C13.2879 9.66093 14.4479 8.71093 16.1809 8.71093C19.7819 8.71093 20.4479 11.0809 20.4479 14.1659L20.4469 20.4519ZM5.33693 7.43293C4.19293 7.43293 3.27393 6.50693 3.27393 5.36793C3.27393 4.22993 4.19393 3.30493 5.33693 3.30493C6.47693 3.30493 7.40093 4.22993 7.40093 5.36793C7.40093 6.50693 6.47593 7.43293 5.33693 7.43293ZM7.11893 20.4519H3.55493V8.99993H7.11893V20.4519Z"
                                                fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            />
                                        </svg>
                                    </Link>
                                )}
                                {corporation && corporation.Twitter && (
                                    <Link href={corporation.Twitter} maxW={6} aria-label="Twitter">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M21.9608 5.90537C21.2234 6.22443 20.4359 6.44179 19.6068 6.54104C20.4517 6.03988 21.1017 5.25001 21.4092 4.30758C20.6167 4.7628 19.7384 5.09417 18.8035 5.27872C18.0568 4.49213 16.9927 4 15.8111 4C13.547 4 11.7113 5.80694 11.7113 8.03301C11.7113 8.3529 11.7488 8.66048 11.8171 8.95494C8.4089 8.79664 5.38819 7.18573 3.36661 4.7505C3.01079 5.3427 2.81163 6.03086 2.81163 6.78054C2.81163 8.18311 3.5366 9.4159 4.63489 10.1402C3.96242 10.1188 3.32994 9.93674 2.7783 9.6349V9.68493C2.7783 11.6412 4.18908 13.2726 6.0665 13.6441C5.72235 13.7352 5.35903 13.7844 4.98654 13.7844C4.72489 13.7844 4.47406 13.7598 4.22324 13.7138C4.74905 15.3157 6.26066 16.4837 8.05975 16.5165C6.65981 17.5984 4.88571 18.2431 2.97496 18.2431C2.64997 18.2431 2.32582 18.2242 2 18.1881C3.82409 19.3315 5.97317 20 8.29724 20C15.8419 20 19.9626 13.8516 19.9626 8.52843C19.9626 8.357 19.9626 8.18393 19.9501 8.01169C20.7509 7.44656 21.45 6.73215 22 5.92177L21.9608 5.90537Z"
                                                fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            />
                                        </svg>
                                    </Link>
                                )}
                                {corporation && corporation.Instagram && (
                                    <Link href={corporation.Instagram} maxW={5} aria-label="Instagram">
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10 0C7.28333 0 6.94417 0.0125 5.8775 0.06C4.8125 0.11 4.0875 0.2775 3.45 0.525C2.7925 0.78 2.23417 1.1225 1.67833 1.67833C1.1225 2.23417 0.779167 2.79167 0.525 3.45C0.2775 4.0875 0.109167 4.8125 0.06 5.8775C0.01 6.94417 0 7.28333 0 10C0 12.7167 0.0125 13.0558 0.06 14.1225C0.11 15.1867 0.2775 15.9125 0.525 16.55C0.78 17.2067 1.1225 17.7658 1.67833 18.3217C2.23417 18.8767 2.79167 19.2208 3.45 19.475C4.08833 19.7217 4.81333 19.8908 5.8775 19.94C6.94417 19.99 7.28333 20 10 20C12.7167 20 13.0558 19.9875 14.1225 19.94C15.1867 19.89 15.9125 19.7217 16.55 19.475C17.2067 19.22 17.7658 18.8767 18.3217 18.3217C18.8767 17.7658 19.2208 17.2092 19.475 16.55C19.7217 15.9125 19.8908 15.1867 19.94 14.1225C19.99 13.0558 20 12.7167 20 10C20 7.28333 19.9875 6.94417 19.94 5.8775C19.89 4.81333 19.7217 4.08667 19.475 3.45C19.22 2.7925 18.8767 2.23417 18.3217 1.67833C17.7658 1.1225 17.2092 0.779167 16.55 0.525C15.9125 0.2775 15.1867 0.109167 14.1225 0.06C13.0558 0.01 12.7167 0 10 0ZM10 1.8C12.6692 1.8 12.9875 1.81333 14.0417 1.85917C15.0167 1.905 15.5458 2.06667 15.8975 2.205C16.3658 2.38583 16.6975 2.6025 17.0492 2.95167C17.3983 3.30167 17.615 3.63417 17.7958 4.1025C17.9325 4.45417 18.0958 4.98333 18.14 5.95833C18.1875 7.01333 18.1983 7.33 18.1983 10C18.1983 12.67 18.1858 12.9875 18.1367 14.0417C18.0858 15.0167 17.9233 15.5458 17.7858 15.8975C17.5992 16.3658 17.3867 16.6975 17.0367 17.0492C16.6875 17.3983 16.35 17.615 15.8867 17.7958C15.5367 17.9325 14.9992 18.0958 14.0242 18.14C12.9625 18.1875 12.65 18.1983 9.975 18.1983C7.29917 18.1983 6.98667 18.1858 5.92583 18.1367C4.95 18.0858 4.4125 17.9233 4.0625 17.7858C3.58833 17.5992 3.2625 17.3867 2.91333 17.0367C2.5625 16.6875 2.33833 16.35 2.16333 15.8867C2.02583 15.5367 1.86417 14.9992 1.81333 14.0242C1.77583 12.9742 1.7625 12.65 1.7625 9.9875C1.7625 7.32417 1.77583 6.99917 1.81333 5.93667C1.86417 4.96167 2.02583 4.425 2.16333 4.075C2.33833 3.6 2.5625 3.275 2.91333 2.92417C3.2625 2.575 3.58833 2.35 4.0625 2.17583C4.4125 2.0375 4.93833 1.875 5.91333 1.825C6.97583 1.7875 7.28833 1.775 9.9625 1.775L10 1.8ZM10 4.865C7.1625 4.865 4.865 7.165 4.865 10C4.865 12.8375 7.165 15.135 10 15.135C12.8375 15.135 15.135 12.835 15.135 10C15.135 7.1625 12.835 4.865 10 4.865ZM10 13.3333C8.15833 13.3333 6.66667 11.8417 6.66667 10C6.66667 8.15833 8.15833 6.66667 10 6.66667C11.8417 6.66667 13.3333 8.15833 13.3333 10C13.3333 11.8417 11.8417 13.3333 10 13.3333ZM16.5383 4.6625C16.5383 5.325 16 5.8625 15.3383 5.8625C14.6758 5.8625 14.1383 5.32417 14.1383 4.6625C14.1383 4.00083 14.6767 3.46333 15.3383 3.46333C15.9992 3.4625 16.5383 4.00083 16.5383 4.6625Z"
                                                fill={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                            />
                                        </svg>
                                    </Link>
                                )}
                            </Flex>
                        </VStack>
                    </VStack>
                </Flex>

                <Flex
                    flexDirection={{ base: 'column-reverse', xl: 'row' }}
                    justifyContent={'space-between'}
                    py={6}
                    gap={{ base: 6, xl: 0 }}
                >
                    <Flex py={6} gap={{ base: 6, xl: 0 }}>
                        <Text
                            maxW={{ base: '200px', xl: '200px' }}
                            width={{ base: 200, xl: 200 }}
                            mr={{ base: 0, xl: '110px' }}
                            fontSize={'xs'}
                            color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                        >
                            <Link
                                href="https://www.recman.io"
                                target="_blank"
                                isExternal
                                color={mainThemeTextColor ? mainThemeTextColor : 'text.link'}
                                _hover={{
                                    textDecoration: 'underline',
                                    // color: 'text.link',
                                }}
                            >
                                {t('9840', 'Career page')}
                            </Link>{' '}
                            {t('2798', 'by')} recman
                        </Text>

                        {/*<Text fontSize={'xs'} color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}>*/}
                        {/*    {t('6654')} {new Date().getFullYear()} -{' '}*/}
                        {/*    {corporation && corporation.Name ? corporation.Name : ''}*/}
                        {/*</Text>*/}
                    </Flex>

                    <HStack gap={6}>
                        <Box
                            as={'a'}
                            href={'/user-agreement'}
                            fontSize={'xs'}
                            color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                            cursor={'pointer'}
                            _hover={{
                                textDecoration: 'underline',
                            }}
                        >
                            {t('4635')}
                        </Box>
                        <Box
                            as={'a'}
                            href={'/privacy-policy'}
                            fontSize={'xs'}
                            color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                            cursor={'pointer'}
                            _hover={{
                                textDecoration: 'underline',
                            }}
                        >
                            {t('4636')}
                        </Box>
                        <Box
                            as={'a'}
                            href={'/cookie-notice'}
                            fontSize={'xs'}
                            color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                            cursor={'pointer'}
                            _hover={{
                                textDecoration: 'underline',
                            }}
                        >
                            {t('4637')}
                        </Box>

                        {appTheme?.customerPortalLink ? (
                            <Box
                                as={'a'}
                                href={appTheme.customerPortalLink}
                                fontSize={'xs'}
                                color={mainThemeTextColor ? mainThemeTextColor : 'text.main'}
                                cursor={'pointer'}
                                _hover={{
                                    textDecoration: 'underline',
                                }}
                            >
                                {t('7948')}
                            </Box>
                        ) : null}
                    </HStack>
                </Flex>
            </Container>
        </Box>
    )
}
